import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Spinner } from '../../../../components/Commons/Spinner/Spinner';
import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { CheckCircle, CircleOutlined } from '@mui/icons-material';
import GenericPromises from '../../../../api/GenericPromises';
import useSnackBar from '../../../../components/Commons/SnackBar/useSnackBar';
import { useTranslation } from 'react-i18next';
import { DialogEntity } from '../../../../components/Dialogs/DialogEntity';
import { Header } from '../../../../components/Header';
import { ConfirmDialog } from './dialogs/confirmDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useParamsFilter } from '../../../../hooks/useParamsFilter';
import { ProjectTaskDashboard } from '../../../../interfaces/Projects/projectTaskDashboard';
import { useTaskStatesStore } from '../../../../store/Projects/TaskStates/TaskStatesStore';
import { Menuitem } from '../../../../interfaces/Security/menu';
import { useProjectTaskDashboardStore } from '../../../../store/Projects/ProjectTaskDashboard/ProjectTaskDashboardStore';
import { FieldType } from '../../../../interfaces/Projects/Catalogs/fieldTypes';
import { useBase64 } from '../../../../hooks/useBase64';
import { AuthContext } from '../../../../context/AuthContext';
import { UpdateProjectTaskDashboard } from '../update';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import NotesIcon from '@mui/icons-material/Notes';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Chip from '@mui/material/Chip';


interface TaskCardProps {
  task: ProjectTaskDashboard,
  color: number[] | undefined;
  isEdit: boolean;
  resourceScreen: Menuitem;
  comboFieldTypes: FieldType[];
}

export const TaskCard = ({
  task,
  color,
  isEdit,
  resourceScreen,
  comboFieldTypes
}: TaskCardProps) => {
  const [t] = useTranslation("global");
  const { GenericPostResource, GenericGetResource, GenericPutResource } = GenericPromises();
  const [dataloaded, setDataLoaded] = useState(false);
  const [onChecked, setOnChecked] = useState(false);
  const [loadingPut, setLoadingPut] = useState(false);
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const [dialogConfirmUncheckTask, setDialogConfirmUncheckTask] = useState(false);
  const { ConvertParamsToBase64 } = useParamsFilter();
  const [taskColor, setTaskColor] = useState<string>();
  const taskStates = useTaskStatesStore(store => store.taskStates);
  const handleUpdateTask = useProjectTaskDashboardStore(store => store.updateProjectTask);
  const handleDeleteTask = useProjectTaskDashboardStore(store => store.deleteProjectTask);
  const [openDialogUpdateTask, setOpenDialogUpdateTask] = useState(false);
  const { utf8ToBase64 } = useBase64();
  const { user } = useContext(AuthContext);
  const isTaskCompleted = useProjectTaskDashboardStore(store => store.isTaskCompleted);
  const setIsTaskCompleted = useProjectTaskDashboardStore(store => store.setIsTaskCompleted);
  const checkIfTaskIsCompleted = useProjectTaskDashboardStore(store => store.checkIfTaskIsCompleted);




  const handleTaskCompletion = () => {
    setLoadingPut(true);

    if (taskStates) {
      const currentState = taskStates.find(
        (state) => state.task_state_id === task.current_task_state_id
      )
      const nextState = taskStates.find((state) =>
        state.task_state_name === (onChecked ? "PENDING" : "DONE")
      );
      if (nextState?.task_state_id && currentState?.task_state_id) {
        const myData = {
          new_task_state_id: nextState.task_state_id,
          project_task_id: task.project_task_id,
          group_id: user?.group_id,
          last_update_user: user?.user_email
        }
        GenericPutResource('/projecttaskdashboard/validatetaskandstage', myData)
          .then((response) => {
            const responseData = response.data;
            if (task.project_task_id) {
              setIsTaskCompleted(task.project_task_id, !onChecked);
            }
            handleUpdateTask(responseData[0].projecttasks[0])
          })
          .catch((error) => {
            if (error.response.data.message) {
              showSnackBar(t(error.response.data.message), "info");
            } else {
              showSnackBar(error.message, "error");
            }
          })
          .finally(() => setLoadingPut(false));
      }
    }
  };


  const openProjectDetails = useCallback(() => {
    const params = ConvertParamsToBase64(`project_id=${task.project_id}`);
    window.open(`/projectsdetails/view?${params}`, '_blank');
  }, [ConvertParamsToBase64]);

  useEffect(() => {
    if (task.project_task_id) {
      setIsTaskCompleted(task.project_task_id, checkIfTaskIsCompleted(task));
      setTaskColor(task.project_task_color);
      setOnChecked(isTaskCompleted[task.project_task_id]);
      setDataLoaded(true);
    }
  }, [task]);

  return (
    <>
      {!dataloaded &&
        <div className="w-100 my-2">
          <Spinner isBox={false} />
        </div>
      }
      {dataloaded && (
        <Grid item key={task.project_task_id} xs={12} sx={{ marginBlock: 2 }}>
          <Box
            sx={{
              border: `2px solid ${taskColor}`,
              borderRadius: `8px`,
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
              justifyContent: "space-between",
              backgroundColor: color
                ? `rgb(${color[0] - 20}, ${color[1] - 20}, ${color[2]}, 0.2)`
                : "rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: color
                  ? `rgb(${color[0] - 20}, ${color[1] - 20}, ${color[2]}, 0.09)`
                  : "rgba(0, 0, 0, 0.1)",
              },
              display: "flex",
              alignItems: "center",
              padding: "8px",
            }}
            onDoubleClick={() => {
              if (task.project_task_id) {
                setOpenDialogUpdateTask(true);
              }
            }}
          >
            <FormControlLabel
              sx={{ marginLeft: 0.1 }}
              control={
                <Checkbox
                  checked={onChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (onChecked) {
                      setOpenDialogUpdateTask(false);
                      setDialogConfirmUncheckTask(true);
                      return
                    }
                    setOpenDialogUpdateTask(false);
                    handleTaskCompletion();
                  }}
                  icon={<CircleOutlined />}
                  checkedIcon={<CheckCircle />}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      color: "primary.main",
                    },
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                  disabled={loadingPut}
                />
              }
              label={
                <>
                </>
              }
              disabled={loadingPut}
            />
            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{
                    fontWeight: 'medium',
                    textDecoration: onChecked ? 'line-through' : 'none',
                    color: onChecked ? 'text.disabled' : 'text.primary',
                    mr: 1
                  }}
                >
                  {task.project_task_name}
                </Typography>
                {task.project_name && (
                  <Chip
                    label={task.project_name}
                    size="small"
                    sx={{
                      mr: 1,
                      mb: 0.5,
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
                {task.project_stage_name && (
                  <Chip
                    label={task.project_stage_name}
                    size="small"
                    sx={{
                      mr: 1,
                      mb: 0.5,
                      backgroundColor: 'secondary.light',
                      color: 'secondary.contrastText',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    ml: 'auto'
                  }}
                >
                  <EventIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {t("projecttaskdashboard.fields.due-date")}: {task.project_task_due_date_evaluated?.toString() || ' N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5, gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <BusinessIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {t("projecttaskdashboard.fields.customer")}: {task.business_name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <PersonIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {t("projecttaskdashboard.fields.project-manager")}: {task.user_name}
                </Typography>
                {task.project_comments && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    <NotesIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                    {t("projecttaskdashboard.fields.notes")}: {task.project_comments}
                  </Typography>
                )}
              </Box>
              {task.project_task_started_date_evaluated && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  <CalendarTodayIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {t("projecttaskdashboard.fields.start-date")}: {task.project_task_started_date_evaluated.toString()}
                </Typography>
              )}
            </Box>
            <FontAwesomeIcon icon={faCircleInfo} style={{ marginLeft: 'auto', cursor: 'pointer', width: 'auto', height: 25, marginRight: 1 }} onClick={openProjectDetails} />
          </Box>
        </Grid>
      )}
      <DialogEntity
        open={dialogConfirmUncheckTask}
        title={<Header title={t("projecttasks.title-view")} />}
        maxWidth={"sm"}
        fullWidth
        content={
          <ConfirmDialog
            projectTaskName={task.project_task_name}
            isEdit={isEdit}
            loadingPut={loadingPut}
            setDialogConfirmUncheckTask={setDialogConfirmUncheckTask}
            handleTaskCompletion={handleTaskCompletion}
          />
        }
      />

      <DialogEntity
        content={
          <UpdateProjectTaskDashboard
            permissions={resourceScreen}
            setOpenDialogUpdateTask={setOpenDialogUpdateTask}
            handleUpdateTask={handleUpdateTask}
            handleDeleteTask={handleDeleteTask}
            project_id={task.project_id}
            projectTaskPayload={task
            }
            comboFieldTypes={comboFieldTypes}
          />
        }
        open={openDialogUpdateTask}
        setOpen={setOpenDialogUpdateTask}
        title={
          <Header
            title={t("projecttasks.title-view")}
            size="sm"
          />
        }
      />
      <SnackbarComponent />
    </>
  )
}
